import React from 'react';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { CanvasDragMirror } from './CanvasDragMirror';
import type { DragData } from '../domain/dnd/types';
import { blockIdsInDragItem } from '../domain/selection/block-bulk-ops';
import { rowIdsInDragItem } from '../domain/selection/row-bulk-ops';
import { getBlockDefinition } from '../domain/blocks/registry';
import { presetById } from '../domain/column-presets';
import { findGroup } from '../domain/find';
import { groupLabel } from '../domain/labels';
import { getSnippetById } from '../domain/snippets';
import type { EmailDocument } from '../domain/types';
import {
  BlockPaletteGlyph,
  SectionPalettePreview,
} from '../shell/panel/addElements/paletteGlyphs';

export function DragGhost({
  drag,
  document,
}: {
  drag: DragData;
  document: EmailDocument;
}) {
  const { t } = useTranslation();
  let body = <div className={"email-builder-drag-ghost"}>{t('Move')}</div>;

  if (drag.kind === 'item-block') {
    body = (
      <CanvasDragMirror
        items={blockIdsInDragItem(drag).map(blockId => ({
          frameSelector: `[data-entity-id="block:${blockId}"]`,
          contentSelector: '[data-frame-content]',
          tightBlockContent: true,
        }))}
      />
    );
  } else if (drag.kind === 'palette-block') {
    body = (
      <PaletteTileGhost label={t(getBlockDefinition(drag.blockType).label)}>
        <BlockPaletteGlyph type={drag.blockType} />
      </PaletteTileGhost>
    );
  } else if (drag.kind === 'item-row') {
    body = (
      <CanvasDragMirror
        items={rowIdsInDragItem(drag).map(rowId => ({
          frameSelector: `[data-entity-id="row:${rowId}"]`,
          contentSelector: '.email-inner',
          useFrameBounds: true,
          preserveDropSlots: true,
        }))}
      />
    );
  } else if (drag.kind === 'item-group') {
    const group = findGroup(document.sections, drag.groupId);
    if (group) {
      body = (
        <CanvasDragMirror
          items={[
            {
              frameSelector: `[data-entity-id="group:${group.id}"]`,
              contentSelector: 'table.email-rail-anchor:not(.email-inner)',
              useFrameBounds: true,
              preserveDropSlots: true,
            },
          ]}
        />
      );
    }
  } else if (drag.kind === 'palette-row') {
    const preset = presetById(drag.presetId);
    body = (
      <PaletteTileGhost label={t(preset.label)}>
        <span className="email-builder-column-palette-preview" aria-hidden="true">
          {preset.ratios.map((ratio, index) => (
            <span
              key={index}
              className="email-builder-column-palette-preview-col"
              style={{ flexGrow: ratio, flexBasis: 0 }}
            />
          ))}
        </span>
      </PaletteTileGhost>
    );
  } else if (drag.kind === 'palette-group') {
    body = (
      <PaletteTileGhost label={t(groupLabel())}>
        <GroupPaletteIcon />
      </PaletteTileGhost>
    );
  } else if (drag.kind === 'palette-section') {
    const snippet = getSnippetById(drag.snippetId);
    body = (
      <PaletteTileGhost label={snippet ? t(snippet.label) : drag.snippetId}>
        <SectionPalettePreview snippetId={drag.snippetId} />
      </PaletteTileGhost>
    );
  }

  if (drag.kind.startsWith('palette-')) {
    return body;
  }

  const isCanvasRow = drag.kind === 'item-row' || drag.kind === 'item-group';
  const isCanvasBlock = drag.kind === 'item-block';

  const shellClassName = classNames(
    "email-builder-drag-ghost__shell",
    isCanvasRow && "email-builder-drag-ghost__shell--canvas-row",
    isCanvasBlock && "email-builder-drag-ghost__shell--canvas-block",
  );

  return (
    <div className={shellClassName}>
      {body}
    </div>
  );
}

function GroupPaletteIcon() {
  return (
    <span className={"email-builder-block-palette-icon-canvas"} aria-hidden>
      <span className={"email-builder-block-palette-icon-group-thumb"}>
        <span className={"email-builder-block-palette-icon-group-row"} />
        <span className={"email-builder-block-palette-icon-group-row"} />
      </span>
    </span>
  );
}

function PaletteTileGhost({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="email-builder-drag-ghost__palette-tile">
      {children}
      <span className="builder-toolbar-palette-item-title">{label}</span>
    </div>
  );
}
